/**
 * Fields (SPEC §4, §5.2, §8): a quiet two-column grid under a tagged node, label left, value right.
 * Under a block it shows the fields that have a value and the required ones; the others are offered
 * while you edit the block (no layout shift on hover). A page shows all its fields. Every edit is
 * one `node.props` op, undoable.
 *
 * Also here: the due chip of a task (SPEC §19 Q7: due inline, the rest in the grid), a page's
 * explicit tags, and the schema editor of a tag page (Phase 1: manual fields, SPEC §8).
 */
import { ChevronDown, ChevronUp, Plus, X } from 'lucide-react'
import { useMemo, useRef, useState, useSyncExternalStore, type KeyboardEvent, type ReactNode } from 'react'
import {
  cryptoRand,
  FIELD_TYPES,
  fieldsOf,
  fieldValue,
  hasTag,
  hasValue,
  isOpenStatus,
  newFieldId,
  normalizeTitle,
  readSchema,
  SCHEMA_FIELD,
  TASK_FIELD,
  titleProblem,
  types,
  uuidv7,
  makeNode,
  op,
  type FieldDef,
  type FieldType,
  type Node,
  type NodeField,
  type NodeId,
  type Schema,
} from '@solar/core'
import { searchTitles } from '../client/picker.ts'
import { dayTitle, todayKey } from './dates.ts'
import { useNode, useStore } from './hooks.ts'
import { navigate } from './router.ts'

function useSchemas(): number {
  const store = useStore()
  return useSyncExternalStore(store.subscribeSchemas, store.getSchemasVersion, store.getSchemasVersion)
}

function useTitlesVersion(): number {
  const store = useStore()
  return useSyncExternalStore(store.titles.subscribe, store.titles.getVersion, store.titles.getVersion)
}

/** The node's visible fields (hidden ones, like a task's status, are shown elsewhere). */
function useFields(node: Node): NodeField[] {
  const store = useStore()
  const sv = useSchemas()
  const tv = useTitlesVersion()
  return useMemo(() => fieldsOf(store.tagEnv, node).filter((f) => !f.field.hidden), [store, node, sv, tv]) // eslint-disable-line react-hooks/exhaustive-deps
}

/** Short date: "Oct 14", with the year when it is not this year. */
export function shortDate(day: string): string {
  const t = dayTitle(day).title // "Oct 14th, 2026"
  const md = t.replace(/(st|nd|rd|th),/, ',')
  return day.slice(0, 4) === todayKey().slice(0, 4) ? md.replace(/, \d{4}$/, '') : md
}

// ------------------------------------------------------------------------------------ grid

export function FieldGrid({ node, active, full = false }: { node: Node; active: boolean; full?: boolean }) {
  const fields = useFields(node)
  const [adding, setAdding] = useState<ReadonlySet<string>>(new Set())
  if (fields.length === 0) return null
  const shown = fields.filter(({ field }) => {
    if (full || field.required) return true
    if (!hasValue(node.props, field)) return adding.has(field.id)
    // A task's due date is a chip next to the text; the grid shows it while editing.
    return field.id !== TASK_FIELD.due || active
  })
  const rest = fields.filter((f) => !shown.includes(f))
  if (shown.length === 0 && !(active && rest.length > 0)) return null
  return (
    <div className="fields" onMouseDown={(e) => e.stopPropagation()}>
      {shown.map(({ field }) => (
        <div key={field.id} className="field">
          <span className="fname">{field.name}</span>
          <FieldValue node={node} field={field} autoEdit={adding.has(field.id) && !hasValue(node.props, field)} />
        </div>
      ))}
      {active && rest.length > 0 && (
        <div className="addfields">
          {rest.map(({ field }) => (
            <button key={field.id} className="link add" onMouseDown={(e) => e.preventDefault()} onClick={() => setAdding(new Set([...adding, field.id]))}>
              <Plus size={12} aria-hidden /> {field.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function FieldValue({ node, field, autoEdit }: { node: Node; field: FieldDef; autoEdit: boolean }) {
  const store = useStore()
  const value = node.props[field.id]
  const set = (input: unknown): boolean => {
    const v = fieldValue(field, input)
    if (v === undefined) return false
    store.setField(node.id, field.id, v)
    return true
  }
  switch (field.type) {
    case 'select':
      return (
        <select className="fval" aria-label={field.name} value={typeof value === 'string' ? value : ''} onChange={(e) => set(e.target.value)}>
          <option value="">—</option>
          {(field.options ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      )
    case 'checkbox':
      return <input className="fval" aria-label={field.name} type="checkbox" checked={value === true} onChange={(e) => set(e.target.checked)} />
    case 'multiselect':
      return <MultiSelect field={field} value={Array.isArray(value) ? (value as string[]) : []} set={set} />
    case 'ref':
      return <RefValue node={node} field={field} value={Array.isArray(value) ? (value as string[]) : typeof value === 'string' ? [value] : []} set={set} autoEdit={autoEdit} />
    default:
      return <TextValue field={field} value={value} set={set} autoEdit={autoEdit} />
  }
}

const INPUT_TYPE: Partial<Record<FieldType, string>> = { number: 'number', date: 'date', datetime: 'datetime-local', url: 'url', email: 'email', phone: 'tel' }

function displayOf(field: FieldDef, value: unknown): ReactNode {
  if (value === undefined || value === null || value === '') return <span className="empty">Empty</span>
  const s = String(value)
  switch (field.type) {
    case 'date':
      return dayTitle(s).title
    case 'datetime':
      return `${dayTitle(s.slice(0, 10)).title}, ${s.slice(11, 16)}`
    case 'url':
      return (
        <a className="ext" href={s} target="_blank" rel="noopener noreferrer nofollow" onClick={(e) => e.stopPropagation()}>
          {s.replace(/^https?:\/\//, '')}
        </a>
      )
    case 'email':
      return (
        <a className="ext" href={`mailto:${s}`} onClick={(e) => e.stopPropagation()}>
          {s}
        </a>
      )
    default:
      return s
  }
}

function TextValue({ field, value, set, autoEdit }: { field: FieldDef; value: unknown; set: (v: unknown) => boolean; autoEdit: boolean }) {
  const [editing, setEditing] = useState(autoEdit)
  const [bad, setBad] = useState(false)
  const done = useRef(false)
  if (!editing) {
    return (
      <span className="fval show" role="button" tabIndex={0} aria-label={field.name} onClick={() => setEditing(true)} onKeyDown={(e) => e.key === 'Enter' && setEditing(true)}>
        {displayOf(field, value)}
      </span>
    )
  }
  const initial = value === undefined || value === null ? '' : field.type === 'datetime' ? String(value).slice(0, 16) : String(value)
  const commit = (raw: string): void => {
    if (done.current) return
    if (set(raw)) {
      done.current = true
      setEditing(false)
    } else setBad(true)
  }
  return (
    <input
      className={bad ? 'fval input bad' : 'fval input'}
      aria-label={field.name}
      type={INPUT_TYPE[field.type] ?? 'text'}
      defaultValue={initial}
      autoFocus
      onFocus={() => (done.current = false)}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') commit(e.currentTarget.value)
        else if (e.key === 'Escape') {
          e.preventDefault()
          done.current = true
          setEditing(false)
        }
      }}
      onChange={() => setBad(false)}
      onBlur={(e) => {
        if (!done.current) commit(e.currentTarget.value)
        if (!done.current) setEditing(false)
      }}
    />
  )
}

function MultiSelect({ field, value, set }: { field: FieldDef; value: readonly string[]; set: (v: unknown) => boolean }) {
  const free = (field.options ?? []).filter((o) => !value.includes(o))
  return (
    <span className="fval chips">
      {value.map((v) => (
        <span key={v} className="chip">
          {v}
          <button className="link x" aria-label={`Remove ${v}`} onClick={() => set(value.filter((x) => x !== v))}>
            <X size={11} />
          </button>
        </span>
      ))}
      {free.length > 0 && (
        <select className="add-select" aria-label={`Add ${field.name}`} value="" onChange={(e) => e.target.value && set([...value, e.target.value])}>
          <option value="">+</option>
          {free.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      )}
    </span>
  )
}

function RefChip({ id, onRemove }: { id: NodeId; onRemove?: () => void }) {
  const n = useNode(id)
  if (!n || n.deletedAt !== null) return null
  return (
    <span className="chip ref-chip">
      <a href={`/n/${id}`} onClick={(e) => (e.preventDefault(), e.stopPropagation(), navigate(`/n/${id}`))}>
        {n.title ?? '(untitled)'}
      </a>
      {onRemove && (
        <button className="link x" aria-label={`Remove ${n.title}`} onClick={onRemove}>
          <X size={11} />
        </button>
      )}
    </span>
  )
}

/** A ref field: chips of the nodes it names, and an input that finds (or creates) more. */
function RefValue({ node, field, value, set, autoEdit }: { node: Node; field: FieldDef; value: readonly string[]; set: (v: unknown) => boolean; autoEdit: boolean }) {
  const store = useStore()
  const [query, setQuery] = useState<string | null>(autoEdit ? '' : null)
  const [active, setActive] = useState(0)
  const canAdd = field.multi || value.length === 0
  const of = field.of ?? null
  const items = useMemo(() => {
    if (query === null) return []
    const found = searchTitles(store.titles, query, 40)
      .filter((n) => n.id !== node.id && !value.includes(n.id) && (of === null || hasTag(store.tagEnv, n, of)))
      .slice(0, 6)
      .map((n) => ({ id: n.id as string | null, title: n.title! }))
    const t = normalizeTitle(query)
    if (t && !titleProblem(t) && store.titles.resolve(t) === null) found.push({ id: null, title: t })
    return found
  }, [query, store, node.id, value, of])
  const pick = (it: { id: string | null; title: string }): void => {
    if (it.id === null) {
      // A new node of the field's type (a person for an assignee), created and set as one undo unit.
      const id = uuidv7()
      const now = Date.now()
      const v = fieldValue(field, field.multi ? [...value, id] : id)
      store.commitOps('field', [
        op.create(makeNode({ id, kind: 'page', title: it.title, tags: of ? [of] : [], createdAt: now, updatedAt: now })),
        op.prop(node.id, field.id, v ?? null),
      ])
    } else set(field.multi ? [...value, it.id] : it.id)
    setQuery(field.multi ? '' : null)
    setActive(0)
  }
  return (
    <span className="fval chips">
      {value.map((id) => (
        <RefChip key={id} id={id} onRemove={() => set(field.multi ? value.filter((x) => x !== id) : null)} />
      ))}
      {canAdd && query === null && (
        <button className="link add" aria-label={`Set ${field.name}`} onClick={() => setQuery('')}>
          {value.length === 0 ? <span className="empty">Empty</span> : <Plus size={12} />}
        </button>
      )}
      {query !== null && (
        <span className="refpick">
          <input
            className="fval input"
            aria-label={field.name}
            autoFocus
            value={query}
            placeholder="Find or create…"
            onChange={(e) => {
              setQuery(e.target.value)
              setActive(0)
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') (e.preventDefault(), setActive((a) => Math.min(a + 1, items.length - 1)))
              else if (e.key === 'ArrowUp') (e.preventDefault(), setActive((a) => Math.max(a - 1, 0)))
              else if (e.key === 'Enter' && items[active]) (e.preventDefault(), pick(items[active]!))
              else if (e.key === 'Escape') (e.preventDefault(), setQuery(null))
            }}
            onBlur={() => setTimeout(() => setQuery(null), 150)}
          />
          {items.length > 0 && (
            <span className="refpick-list" role="listbox">
              {items.map((it, i) => (
                <span
                  key={it.id ?? 'create'}
                  role="option"
                  aria-selected={i === active}
                  className={i === active ? 'item active' : 'item'}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    pick(it)
                  }}
                >
                  {it.id === null ? `Create “${it.title}”` : it.title}
                </span>
              ))}
            </span>
          )}
        </span>
      )}
    </span>
  )
}

// ------------------------------------------------------------------------------------ due chip

export function DueChip({ node }: { node: Node }) {
  const store = useStore()
  const due = node.props[TASK_FIELD.due]
  if (typeof due !== 'string') return null
  const status = store.tasks.statusOf(node)
  if (status === null) return null
  const today = todayKey()
  const cls = !isOpenStatus(status) ? 'chip due' : due < today ? 'chip due overdue' : due === today ? 'chip due today' : 'chip due'
  return (
    <span className={cls} title={`Due ${dayTitle(due).title}`}>
      {shortDate(due)}
    </span>
  )
}

// ------------------------------------------------------------------------------------ page tags

/** A page's explicit tags as chips, with "+ Tag" (types first). */
export function PageTags({ node }: { node: Node }) {
  const store = useStore()
  useSchemas()
  const [query, setQuery] = useState<string | null>(null)
  const items = useMemo(() => {
    if (query === null) return []
    const q = query.trim().toLowerCase()
    return types(store.g)
      .filter((t) => t.id !== node.id && !node.tags.includes(t.id) && t.title!.toLowerCase().includes(q))
      .slice(0, 8)
  }, [query, store, node])
  return (
    <div className="pagetags">
      {node.tags.map((t) => (
        <TagChip key={t} id={t} onRemove={() => store.setTags(node.id, node.tags.filter((x) => x !== t))} />
      ))}
      {query === null ? (
        <button className="link add" onClick={() => setQuery('')}>
          <Plus size={12} aria-hidden /> Tag
        </button>
      ) : (
        <span className="refpick">
          <input
            className="fval input"
            aria-label="Add a tag"
            autoFocus
            value={query}
            placeholder="person, company, …"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && items[0]) {
                e.preventDefault()
                store.setTags(node.id, [...node.tags, items[0].id])
                setQuery(null)
              } else if (e.key === 'Escape') setQuery(null)
            }}
            onBlur={() => setTimeout(() => setQuery(null), 150)}
          />
          {items.length > 0 && (
            <span className="refpick-list" role="listbox">
              {items.map((t, i) => (
                <span
                  key={t.id}
                  role="option"
                  aria-selected={i === 0}
                  className={i === 0 ? 'item active' : 'item'}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    store.setTags(node.id, [...node.tags, t.id])
                    setQuery(null)
                  }}
                >
                  #{t.title}
                </span>
              ))}
            </span>
          )}
        </span>
      )}
    </div>
  )
}

function TagChip({ id, onRemove }: { id: NodeId; onRemove: () => void }) {
  const n = useNode(id)
  if (!n || n.deletedAt !== null) return null
  return (
    <span className="tag chip-tag">
      <a href={`/n/${id}`} onClick={(e) => (e.preventDefault(), navigate(`/n/${id}`))}>
        #{n.title}
      </a>
      <button className="link x" aria-label={`Remove #${n.title}`} onClick={onRemove}>
        <X size={11} />
      </button>
    </span>
  )
}

// ------------------------------------------------------------------------------------ schema

const TYPE_LABEL: Record<FieldType, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  datetime: 'Date and time',
  select: 'Select',
  multiselect: 'Multi-select',
  checkbox: 'Checkbox',
  url: 'URL',
  email: 'Email',
  phone: 'Phone',
  ref: 'Reference',
}

/** The tag page's schema editor (SPEC §8): add, rename, retype, reorder and remove fields; inherit. */
export function SchemaEditor({ node }: { node: Node }) {
  const store = useStore()
  useSchemas()
  const schema = readSchema(node.props[SCHEMA_FIELD])
  const [open, setOpen] = useState(node.kind === 'tag')
  if (!schema) {
    return (
      <div className="schema">
        <button className="link add" onClick={() => store.setSchema(node.id, { fields: [], extends: [] })}>
          <Plus size={12} aria-hidden /> Add fields (make #{node.title} a type)
        </button>
      </div>
    )
  }
  const save = (next: Partial<Schema>): void => store.setSchema(node.id, { ...schema, ...next })
  const setField = (i: number, f: FieldDef): void => save({ fields: schema.fields.map((x, j) => (j === i ? f : x)) })
  const move = (i: number, d: number): void => {
    const fields = [...schema.fields]
    const [f] = fields.splice(i, 1)
    fields.splice(i + d, 0, f!)
    save({ fields })
  }
  const all = types(store.g).filter((t) => t.id !== node.id && !schema.extends.includes(t.id))
  return (
    <div className="schema">
      <button className="link schema-head" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="caret">{open ? '▾' : '▸'}</span> {schema.fields.length} field{schema.fields.length === 1 ? '' : 's'}
        {schema.extends.length > 0 && ' + inherited'}
      </button>
      {open && (
        <div className="schema-body">
          <div className="schema-ext">
            Inherits from:
            {schema.extends.map((t) => (
              <TagChip key={t} id={t} onRemove={() => save({ extends: schema.extends.filter((x) => x !== t) })} />
            ))}
            <select aria-label="Inherit from" value="" onChange={(e) => e.target.value && save({ extends: [...schema.extends, e.target.value] })}>
              <option value="">+</option>
              {all.map((t) => (
                <option key={t.id} value={t.id}>
                  #{t.title}
                </option>
              ))}
            </select>
          </div>
          {schema.fields.map((f, i) => (
            <FieldDefRow
              key={f.id}
              f={f}
              first={i === 0}
              last={i === schema.fields.length - 1}
              onChange={(nf) => setField(i, nf)}
              onMove={(d) => move(i, d)}
              onRemove={() => save({ fields: schema.fields.filter((_, j) => j !== i) })}
            />
          ))}
          <button className="link add" onClick={() => save({ fields: [...schema.fields, { id: newFieldId(cryptoRand), name: `Field ${schema.fields.length + 1}`, type: 'text' }] })}>
            <Plus size={12} aria-hidden /> Add field
          </button>
        </div>
      )}
    </div>
  )
}

function FieldDefRow(p: { f: FieldDef; first: boolean; last: boolean; onChange: (f: FieldDef) => void; onMove: (d: number) => void; onRemove: () => void }) {
  const store = useStore()
  const { f } = p
  const all = types(store.g)
  return (
    <div className="fdef" data-field={f.id}>
      <input
        className="fval input"
        aria-label="Field name"
        defaultValue={f.name}
        onBlur={(e) => e.currentTarget.value.trim() && e.currentTarget.value.trim() !== f.name && p.onChange({ ...f, name: e.currentTarget.value.trim() })}
        onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
      />
      <select aria-label="Field type" value={f.type} onChange={(e) => p.onChange({ ...f, type: e.target.value as FieldType })}>
        {FIELD_TYPES.map((t) => (
          <option key={t} value={t}>
            {TYPE_LABEL[t]}
          </option>
        ))}
      </select>
      {(f.type === 'select' || f.type === 'multiselect') && (
        <input
          className="fval input"
          aria-label="Options"
          placeholder="Options, comma-separated"
          defaultValue={(f.options ?? []).join(', ')}
          onBlur={(e) => p.onChange({ ...f, options: e.currentTarget.value.split(',').map((x) => x.trim()).filter(Boolean) })}
          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
        />
      )}
      {f.type === 'ref' && (
        <>
          <select aria-label="Refers to" value={f.of ?? ''} onChange={(e) => p.onChange({ ...f, of: e.target.value || null })}>
            <option value="">any page</option>
            {all.map((t) => (
              <option key={t.id} value={t.id}>
                #{t.title}
              </option>
            ))}
          </select>
          <label className="flag">
            <input type="checkbox" checked={f.multi === true} onChange={(e) => p.onChange({ ...f, multi: e.target.checked || undefined })} /> several
          </label>
        </>
      )}
      <label className="flag">
        <input type="checkbox" checked={f.required === true} onChange={(e) => p.onChange({ ...f, required: e.target.checked || undefined })} /> required
      </label>
      {f.hidden && <span className="muted">shown as the task prefix</span>}
      <span className="fdef-tools">
        <button className="link" aria-label="Move up" disabled={p.first} onClick={() => p.onMove(-1)}>
          <ChevronUp size={14} />
        </button>
        <button className="link" aria-label="Move down" disabled={p.last} onClick={() => p.onMove(1)}>
          <ChevronDown size={14} />
        </button>
        <button className="link" aria-label={`Remove ${f.name}`} onClick={p.onRemove}>
          <X size={14} />
        </button>
      </span>
    </div>
  )
}
